<template>
  <div ref="root" class="sp-omnisearch">
    <input
      ref="searchField"
      v-model="searchString"
      type="text"
      :data-testid="inputTestId"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="isReadonly"
      part="input"
      autocomplete="off"
      @change="handleSearchChange"
    />
    <slot ref="filterInputs" name="filters"></slot>
  </div>
</template>

<script setup>
/**
 * Omnisearch is an web component designed to display multi-faceted search suggestions, and direct links to content. It
 * Integrates with forms and can submit multiple form parameters if the selected option requires it. It also provides
 * callbacks to respond to user selection if form integration is not needed.
 *
 * @displayName OmniSearch
 * @group Custom Elements
 * @component sp-omnisearch
 */

import { isPresent } from "#ce/utils/props";
import { computed, onMounted, ref, toRef, useHost, useShadowRoot, useTemplateRef, watch } from "vue";
import { createApiClient } from "#/shared/api/client";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { formAssociatedHost } from "#ce/framework/utils";
import { props as propsDefinitions } from "./props.js";

// sp-omnisearch submits form values via useForm()/ElementInternals.
defineOptions(formAssociatedHost());

const api = createApiClient({ parse: false });

const root = useTemplateRef("root");
const searchField = useTemplateRef("searchField");
const filterInputs = useTemplateRef("filterInputs");
const autocomplete = ref(null);
const data = ref(null);
const selectedItem = ref(null);
const searchString = ref(null);
const formValue = ref(null);
const { mirrorFormValue, onFormReset } = useForm();
const host = useHost();
const shadowRoot = useShadowRoot();

const props = defineProps(propsDefinitions);

// Reconcile disabled (presence-based own attribute OR ancestor <fieldset disabled>)
// as the single source of truth, since sp-omnisearch is form-associated.
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

const isReadonly = computed(() => isPresent(props.readonly));

// BEHAVIOUR

// Load the autocomplete
onMounted(async () => {
  refreshData();
  loadAutocomplete();
});

// When the src attribute is changed, get new data
watch(() => props.src, refreshData);

// When the data changes and the autocomplete is ready, update the autocomplete data
watch(() => [data.value, autocomplete.value], assignData);

// Update the search value when the search property changes
watch(
  () => props.search,
  () => (searchString.value = props.search),
);

watch(selectedItem, () => {
  slotSelectedItemFilters();

  // An empty selection carries no text and no filters — a reset and a scripted write land here,
  // and both manage the form value themselves.
  if (!selectedItem.value) {
    return;
  }

  // A selected item replaces the query: the element submits the item's filters, not text.
  formValue.value = null;
  searchString.value = selectedItem.value.text;

  // This event differs from select in that it is only fired if the selected item changes.
  emitBubblingEvent("change", cloneItem(selectedItem.value));
});

mirrorFormValue(formValue);

// A reset returns the element to the query its `search` attribute describes and drops the
// selection, the way a native control returns to its own default — silently.
onFormReset(() => {
  selectedItem.value = null;
  searchString.value = props.search ?? null;
  formValue.value = null;
});

const { exposeProperties } = useExpose();

exposeProperties({
  value: {
    configurable: true,
    get: () => formValue.value,
    // Writable like any form control's value.
    set: adoptQuery,
  },
  selectedItem: {
    configurable: true,
    get: () => selectedItem.value,
  },
});

// Adopts the text as the committed query, silently, dropping any selection.
function adoptQuery(value) {
  selectedItem.value = null;
  searchString.value = value ?? null;
  formValue.value = value ?? null;
}

// Enable/Disable the hidden filter inputs
watch(disabled, slotSelectedItemFilters);

// INITIALIZE

// Initialize the search string from the element attribute
searchString.value = props.search;

// EVENT HANDLERS

function handleSearchChange() {
  clearSlottedFilters();
  // Editing the text after picking a suggestion abandons that selection: the element submits the
  // query, not the stale item's filters.
  selectedItem.value = null;
  formValue.value = searchString.value;
  emitBubblingEvent("change", formValue.value);
}

function handleAutocompleteSelect(item) {
  // Clone the item so the original cannot be modified unintentionally
  item = cloneItem(item);

  const handlerReturnValue = runOnAutocompleteSelect(item);

  // If the handler returns false, cancel the event
  if (handlerReturnValue === false) {
    return; // Cancel the selection
  }

  // Emit the event before the possibility that the handler replaces it
  emitBubblingEvent("select", item);

  if (handlerReturnValue !== undefined) {
    item = handlerReturnValue; // Replace the item with the new object
  }

  if (item.url) {
    navigateToUrlUsingAnchorTag(item.url);
  } else {
    selectedItem.value = item;
  }
}

// HELPER FUNCTIONS

function runOnAutocompleteSelect(item) {
  const handlerCode = props.onselect;
  if (handlerCode) {
    // Prefer Function constructor over eval for security reasons
    const handler = new Function("item", handlerCode);
    return handler.call(host, item);
  }
}

// Create hidden form inputs in the light DOM for the selected item's filters
function slotSelectedItemFilters() {
  const newFilters = selectedItem.value?.filters;

  if (newFilters) {
    slotFilters(newFilters);
  } else {
    clearSlottedFilters();
  }
}

// Create hidden form inputs in the light DOM for the given filters
function slotFilters(filters) {
  clearSlottedFilters();

  for (const [name, value] of Object.entries(filters)) {
    const input = document.createElement("input");
    input.setAttribute("slot", "filters");
    input.setAttribute("type", "hidden");
    input.setAttribute("name", name);
    input.setAttribute("value", value);
    if (disabled.value) {
      input.setAttribute("disabled", "");
    }
    host.append(input); // Append a new hidden input for the slot
  }
}

// Clear the slotted inputs
function clearSlottedFilters() {
  filterInputs.value.assignedElements().forEach((element) => element.remove());
}

// Uses an anchor tag to simulate a link click in the Light DOM. This allows event handlers to intercept the click if
// desired.
function navigateToUrlUsingAnchorTag(url) {
  const a = document.createElement("a");
  a.href = url;
  host.appendChild(a);
  a.click();
  a.remove();
}

// Create and dispatch a native event that bubbles
function emitBubblingEvent(eventType, payload) {
  const event = new CustomEvent(eventType, {
    detail: payload,
    bubbles: true,
  });

  host.dispatchEvent(event);
}

function assignData() {
  if (autocomplete.value && data.value) {
    autocomplete.value.setData(data.value);
  }
}

// Clone the item so the original cannot be modified unintentionally
function cloneItem(item) {
  return Object.assign({}, item);
}

async function refreshData() {
  if (!props.src) {
    data.value = [];
    return;
  }

  try {
    data.value = await api.request("get", props.src);
  } catch {
    data.value = [];
  }
}

async function loadAutocomplete() {
  const resolveUrl = (url) => {
    if (url && typeof url === "object" && "value" in url) {
      return url.value;
    }
    return url;
  };
  const jsUrl = resolveUrl(props.autocompleteJsUrl);
  const cssUrl = resolveUrl(props.autocompleteCSSUrl);

  const jsFetch = window.Sparkle.Codeload.importIf(!window.DoctorFinder?.Views?.Autocomplete, jsUrl);

  const cssContainer = shadowRoot || root.value;
  const cssFetch = window.Sparkle.Codeload.custom({ cssContainer }).import(cssUrl, { type: "css" });

  await Promise.all([jsFetch, cssFetch]);

  autocomplete.value = new window.DoctorFinder.Views.Autocomplete(searchField.value, [], {
    onSelect: handleAutocompleteSelect,
  });
  autocomplete.value.render();
}
</script>
